import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUser } from './UserContext';
import settingsService from '../services/SettingsService';
import { 
  setCompanyBrand,
  getPlatformBrand, 
  getCurrentBrand,
  getCurrentBrandName,
  getBrandTerm,
  resetBrandCache,
  captureCompanyBrandFromPlatform,
  getBrandingDebugInfo
} from '../utils/brandingUtils';

const BrandContext = createContext();

// The company's brand, read off the session's companies row. SettingsService owns that row
// (one read per session, shared with sign-in); this used to be its own /companies?select=brand
// request on every load. Same outcome as brandingUtils.loadCompanyBrand: null when the row
// can't be read, so the platform brand stays.
async function loadCompanyBrandFromProfile(companyId, fresh = false) {
  const company = await settingsService.getCompanyProfile(companyId, { fresh });
  if (!company || Array.isArray(company)) return null;
  const brandCode = company.brand || 'tradesmate';
  setCompanyBrand(brandCode);
  return brandCode;
}

const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

function applyBrandTokens(brand) {
  if (typeof document === 'undefined' || !brand) return;

  const root = document.documentElement;
  const primaryColor = HEX_COLOR_PATTERN.test(brand.primaryColor || '') ? brand.primaryColor : null;
  const secondaryColor = HEX_COLOR_PATTERN.test(brand.secondaryColor || '') ? brand.secondaryColor : null;

  if (!primaryColor) {
    console.warn('BrandContext: invalid primary brand color; using theme defaults');
    root.style.removeProperty('--brand-primary');
    root.style.removeProperty('--brand-secondary');
    return;
  }

  root.style.setProperty('--brand-primary', primaryColor);
  root.style.setProperty('--brand-secondary', secondaryColor || primaryColor);
  root.dataset.brand = brand.code || 'tradesmate';
}

export function BrandProvider({ children }) {
  const { user } = useUser();
  const [brandLoaded, setBrandLoaded] = useState(false);
  const [currentBrand, setCurrentBrand] = useState(getPlatformBrand());

  // Keep visual branding separate from the auth/session handoff. A styling problem
  // must never prevent the user or company context from finishing loading.
  useEffect(() => {
    applyBrandTokens(currentBrand);
  }, [currentBrand]);

  // Load company brand when user logs in (ONLY handoff point)
  useEffect(() => {
    async function loadBrand() {
      if (user?.company_id) {
        // Company context exists - load company brand (ignore hostname)
        try {
          const brandCode = await loadCompanyBrandFromProfile(user.company_id);
          if (brandCode) {
            setCurrentBrand(getCurrentBrand());
          }
        } catch (error) {
          console.error('Failed to load company brand:', error);
        } finally {
          // Keep the platform brand as fallback when the company lookup fails.
          setBrandLoaded(true);
        }
      } else {
        // No company context - use platform brand (from hostname)
        resetBrandCache();
        setCurrentBrand(getPlatformBrand());
        setBrandLoaded(true);
      }
    }

    loadBrand();
  }, [user?.company_id]);

  const value = {
    brand: currentBrand,
    brandLoaded,
    // Keyed on code, not the display name — see brandingUtils.brandCodeOf. Comparing
    // against 'SmbMate' here silently returned false everywhere the name was spelled
    // 'SMBMate', which is how the DB and the portal spell it.
    isTradesMatePro: currentBrand.code === 'tradesmate',
    isSmbMate: currentBrand.code === 'smbmate',

    // Brand vocabulary. `terms` for direct access, `t(key)` for the common case:
    //   const { t } = useBrand();  <h3>{t('workers')}</h3>
    // Only genuinely trade-specific nouns live here — see TRADE_TERMS in brandingUtils.
    // "Job", "customer", "quote" and "invoice" are intentionally NOT swappable; they
    // already read correctly for every business type.
    terms: currentBrand.terms || {},
    t: (key) => getBrandTerm(currentBrand.code, key),

    // Helper methods
    getBrandName: () => currentBrand.name,
    getBrandColor: () => currentBrand.primaryColor,
    getBrandLogo: () => currentBrand.logo,
    getSupportEmail: () => currentBrand.supportEmail,
    
    // Context-aware branding
    hasCompanyContext: () => !!user?.company_id,
    isUsingPlatformBrand: () => !user?.company_id,
    isUsingCompanyBrand: () => !!user?.company_id,
    
    // For company creation (capture platform brand)
    captureForNewCompany: captureCompanyBrandFromPlatform,
    
    // Debug info
    getDebugInfo: getBrandingDebugInfo,
    
    // Force refresh (for admin switching)
    refreshBrand: async () => {
      if (user?.company_id) {
        const brandCode = await loadCompanyBrandFromProfile(user.company_id, true);
        if (brandCode) {
          setCurrentBrand(getCurrentBrand());
        }
      }
    }
  };

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
}

// Hook for components that need to wait for brand loading
export function useBrandReady() {
  const { brand, brandLoaded } = useBrand();
  return { brand, isReady: brandLoaded };
}

// Hook for marketplace components (platform branding only).
//
// brandName/brandColors are NOT convenience extras — both callers
// (PublicMarketplace, PublicRequestDetails) already destructured them and this hook
// never returned them. So `brandName` rendered as nothing ("Join  and start winning
// more projects") and PublicRequestDetails did `brandColors.gradient` on undefined,
// which threw a TypeError and white-screened every public request page that loaded
// successfully. The not-found path returns before that line, which is why it survived.
export function usePlatformBrand() {
  const brand = getPlatformBrand();
  return {
    brand,
    brandName: brand.name,
    brandColors: { gradient: brand.gradient, primary: brand.primaryColor, secondary: brand.secondaryColor },
    // Code, not display name — same reason as the provider value above.
    isTradesMatePro: brand.code === 'tradesmate',
    isSmbMate: brand.code === 'smbmate'
  };
}
